import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBox,
  FaBoxOpen,
  FaCalculator,
  FaClipboardList,
  FaDollarSign,
  FaIndustry,
  FaLayerGroup,
  FaPaperclip,
  FaShieldAlt,
  FaSyncAlt,
  FaUser,
} from "react-icons/fa";
import WebsiteTopBar from "../../components/WebsiteTopBar";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import { useFactoryDetailsMaster } from "../../hooks/useFactoryDetailsMaster";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { createCmRfqDisplayResolver } from "../../utils/cmRfqDisplayValues";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/website-top-bar.scss";
import "../../assets/styles/cm_rfq_submitted_view.scss";

const FIELD_LABELS = {
  companyName: "Company Name",
  contactName: "Contact Name",
  email: "Email",
  phone: "Phone",
  technicalContact: "Technical Contact",
  commercialContact: "Commercial Contact",
  preferredCommunication: "Preferred Communication",
  title: "Title",
  industryType: "Industry Type",
  manufacturingProcessFacilities: "Manufacturing Process Facilities",
  ndaRequired: "NDA Required",
  expectedStartDate: "Expected Start Date",
  frequencyOfManufacturing: "Frequency of Manufacturing",
  estimatedOrderVolume: "Estimated Order Volume",
  estimatedAnnualUsage: "Estimated Annual Usage",
  lifetimeVolume: "Lifetime Volume",
  targetCost: "Target Cost",
  incoterms: "Incoterms",
  paymentTerms: "Payment Terms",
  paymentMethod: "Payment Method",
  portOfShipping: "Port Of Shipping",
  deliveryMethods: "Delivery Methods",
  preferredPricingModel: "Preferred Pricing Model",
  unitOfMeasurement: "Unit Of Measurement",
  priceValidityDays: "Price Validity Days",
  expectedDispatchDate: "Expected Dispatch Date",
  priceNegotiable: "Price Negotiable",
  toolingNrePossible: "Tooling Nre Possible",
  toolingOwnership: "Tooling Ownership",
  price: "Price",
  currency: "Currency",
  moq: "Moq",
  cgst: "Cgst",
  sgst: "Sgst",
  igst: "Igst",
  vat: "Vat",
  cess: "Cess",
  otherTaxes: "Other Taxes",
  rev: "Rev",
  uom: "Uom",
  material: "Material",
  partName: "Part Name",
  quantity: "Quantity",
  partNumber: "Part Number",
  filters: "Filters",
  remarks: "Remarks",
  tierLabel: "Tier Label",
  unitPrice: "Unit Price",
  minQuantity: "Min Quantity",
  regulatoryClaims: "Regulatory Claims",
  certificateRequired: "Certificate Required",
  testMethodsStandards: "Test Methods Standards",
  acceptableQualityLevel: "Acceptable Quality Level",
  inspectionRequiredDocument: "Inspection Required Document",
  coldChain: "Cold Chain",
  labellingBarcoding: "Labelling Barcoding",
  preferredTransport: "Preferred Transport",
  packagingTestMethods: "Packaging Test Methods",
  shipToCountryPostcode: "Ship To Country Postcode",
  packagingSpecification: "Packaging Specification",
  warranty: "Warranty",
  guarantee: "Guarantee",
  sampleCost: "Sample Cost",
  sampleType: "Sample Type",
  samplePrice: "Sample Price",
  sampleOffered: "Sample Offered",
  sampleRemarks: "Sample Remarks",
  sampleCurrency: "Sample Currency",
  warrantyPeriod: "Warranty Period",
  guaranteePeriod: "Guarantee Period",
  warrantyRemarks: "Warranty Remarks",
  guaranteeRemarks: "Guarantee Remarks",
  sampleReturnPolicy: "Sample Return Policy",
  sampleShippingMode: "Sample Shipping Mode",
  dispatchLeadTimeDays: "Dispatch Lead Time Days",
};

function humanizeKey(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function getObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function buildFields(obj, keys, resolveDisplay) {
  const source = getObj(obj);
  const entries = keys
    ? keys.filter((k) => source[k] != null && source[k] !== "")
    : Object.keys(source).filter((k) => !k.startsWith("_") && source[k] != null && source[k] !== "");
  return entries.map((key) => ({
    key,
    label: humanizeKey(key),
    value: resolveDisplay(key, source[key]),
    raw: source[key],
  }));
}

function InfoGrid({ fields }) {
  if (!fields.length) {
    return <p className="cm-rfv-empty">No details recorded.</p>;
  }
  return (
    <div className="cm-rfv-grid">
      {fields.map(({ key, label, value, raw }) => (
        <div className="cm-rfv-field" key={key}>
          <span className="cm-rfv-label">{label}</span>
          {key === "email" && raw ? (
            <a className="cm-rfv-link" href={`mailto:${String(raw).trim()}`}>
              {value}
            </a>
          ) : (
            <span className="cm-rfv-value">{value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionCard({ icon, tone = "blue", title, subtitle, children }) {
  return (
    <section className="cm-rfv-section">
      <div className="cm-rfv-section-head">
        <div className={`cm-rfv-section-icon cm-rfv-section-icon--${tone}`}>{icon}</div>
        <div className="cm-rfv-section-titles">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="cm-rfv-section-body">{children}</div>
    </section>
  );
}

const BUSINESS_KEYS = [
  "companyName",
  "contactName",
  "email",
  "phone",
  "technicalContact",
  "commercialContact",
  "preferredCommunication",
];
const PROJECT_KEYS = [
  "title",
  "industryType",
  "manufacturingProcessFacilities",
  "ndaRequired",
  "expectedStartDate",
  "frequencyOfManufacturing",
  "estimatedOrderVolume",
  "estimatedAnnualUsage",
  "lifetimeVolume",
  "targetCost",
];
const COMMERCIAL_KEYS = [
  "incoterms",
  "paymentTerms",
  "paymentMethod",
  "portOfShipping",
  "deliveryMethods",
  "preferredPricingModel",
  "unitOfMeasurement",
  "priceValidityDays",
  "expectedDispatchDate",
  "priceNegotiable",
  "toolingNrePossible",
  "toolingOwnership",
];
const BASE_PRICE_KEYS = ["price", "currency", "moq", "cgst", "sgst", "igst", "vat", "cess", "otherTaxes"];
const PRODUCT_KEYS = ["rev", "uom", "material", "partName", "quantity", "partNumber", "filters"];
const QUALITY_KEYS = [
  "regulatoryClaims",
  "certificateRequired",
  "testMethodsStandards",
  "acceptableQualityLevel",
  "inspectionRequiredDocument",
];
const PACKAGING_KEYS = [
  "coldChain",
  "labellingBarcoding",
  "preferredTransport",
  "packagingTestMethods",
  "shipToCountryPostcode",
  "packagingSpecification",
];
const SAMPLE_KEYS = [
  "warranty",
  "guarantee",
  "sampleCost",
  "sampleType",
  "samplePrice",
  "sampleOffered",
  "sampleRemarks",
  "sampleCurrency",
  "warrantyPeriod",
  "guaranteePeriod",
  "warrantyRemarks",
  "guaranteeRemarks",
  "sampleReturnPolicy",
  "sampleShippingMode",
  "dispatchLeadTimeDays",
];
const TIER_KEYS = ["remarks", "currency", "tierLabel", "unitPrice", "minQuantity"];

function formatStatus(status) {
  const s = String(status || "submitted").trim();
  if (!s) return "Submitted";
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
}

function formatSubmittedAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function resolveBackNav(from) {
  if (from === "p2p") {
    return { href: null, label: "Back to P2P interaction", useHistory: true };
  }
  if (from === "quotes") {
    return { href: "/dashboard/buyer/contract-manufacturers/quotes", label: "Back to quotations", useHistory: false };
  }
  return {
    href: "/dashboard/buyer/contract-manufacturers",
    label: "Back to contract manufacturing",
    useHistory: false,
  };
}

export default function ContractManufacturerRfqSubmittedViewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const from = (searchParams.get("from") || "").trim().toLowerCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const backNav = useMemo(() => resolveBackNav(from), [from]);

  const { dimensionUnitOptions, attributeUnitOptions } = useProductDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();
  const {
    paymentMethodOptions,
    paymentScheduleOptions,
    shippingTermsOptions,
    modeOfTransportOptions,
  } = useFactoryDetailsMaster();

  const resolveDisplay = useMemo(
    () =>
      createCmRfqDisplayResolver({
        shippingTermsOptions,
        paymentScheduleOptions,
        paymentMethodOptions,
        modeOfTransportOptions,
        dimensionUnitOptions,
        attributeUnitOptions,
        currencyOptions,
      }),
    [
      shippingTermsOptions,
      paymentScheduleOptions,
      paymentMethodOptions,
      modeOfTransportOptions,
      dimensionUnitOptions,
      attributeUnitOptions,
      currencyOptions,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setError(null);
      setData(null);
      const numId = id != null ? parseInt(String(id), 10) : NaN;
      if (!Number.isFinite(numId) || numId <= 0) {
        setLoading(false);
        setError("Invalid link. Open View source RFQ from your P2P transaction or CM dashboard.");
        return;
      }
      setLoading(true);
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.RFQ_SUBMISSION(numId));
        if (cancelled) return;
        const row = res?.data?.data ?? res?.data ?? res;
        if (row && typeof row === "object" && (row.id != null || row.payload != null)) {
          setData(row);
        } else {
          setError(res?.message || "Could not load RFQ submission.");
        }
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Could not load RFQ submission.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const payload = getObj(data?.payload);
  const business = getObj(payload.business);
  const project = getObj(payload.project);
  const commercial = getObj(payload.commercial);
  const technical = getObj(payload.technical);
  const quality = getObj(payload.quality);
  const packaging = getObj(payload.packaging);
  const sample = getObj(payload.sample);
  const basePrice = getObj(payload.basePrice);
  const products = Array.isArray(payload.products) ? payload.products : [];
  const tierBreaks = Array.isArray(payload.tierBreaks) ? payload.tierBreaks : [];
  const uploadedFiles = getObj(payload.uploadedFiles);

  const rfqLabel = data?.id != null ? `CM RFQ #${data.id}` : "CM RFQ";
  const projectTitle = project.title || rfqLabel;
  const statusLabel = formatStatus(data?.status);
  const submittedAt = data?.created_at || data?.updated_at || null;
  const manufacturerName =
    data?.manufacturer_company_name ||
    business.companyName ||
    "—";

  const attachmentFields = Object.entries(uploadedFiles)
    .filter(([, url]) => url != null && String(url).trim() !== "")
    .map(([key, url]) => ({
      key,
      label: humanizeKey(key),
      url: String(url),
    }));

  return (
    <div className="cm-rfv-page">
      <WebsiteTopBar />
      <main className="cm-rfv-main">
        {backNav.useHistory ? (
          <button type="button" className="cm-rfv-back" onClick={() => navigate(-1)}>
            <FaArrowLeft aria-hidden />
            {backNav.label}
          </button>
        ) : (
          <Link to={backNav.href} className="cm-rfv-back">
            <FaArrowLeft aria-hidden />
            {backNav.label}
          </Link>
        )}

        {loading ? <p className="text-muted">Loading submitted RFQ…</p> : null}
        {!loading && error ? <div className="alert alert-warning">{error}</div> : null}

        {!loading && !error && data ? (
          <>
            <header className="cm-rfv-summary">
              <p className="cm-rfv-summary-kicker">Contract manufacturing RFQ</p>
              <h1 className="cm-rfv-summary-title">{projectTitle}</h1>
              <div className="cm-rfv-summary-meta">
                <div className="cm-rfv-summary-field">
                  <span className="cm-rfv-summary-label">RFQ Reference No.</span>
                  <span className="cm-rfv-summary-value">{rfqLabel}</span>
                </div>
                <div className="cm-rfv-summary-field">
                  <span className="cm-rfv-summary-label">Status</span>
                  <span className="cm-rfv-status">{statusLabel}</span>
                </div>
                <div className="cm-rfv-summary-field">
                  <span className="cm-rfv-summary-label">Submitted</span>
                  <span className="cm-rfv-summary-value">{formatSubmittedAt(submittedAt)}</span>
                </div>
                <div className="cm-rfv-summary-field">
                  <span className="cm-rfv-summary-label">Manufacturer</span>
                  <span className="cm-rfv-summary-value">{manufacturerName}</span>
                </div>
              </div>
            </header>

            <div className="cm-rfv-columns">
              <div className="cm-rfv-col">
                <SectionCard
                  icon={<FaUser />}
                  tone="orange"
                  title="Business Information"
                  subtitle="Buyer contact details"
                >
                  <InfoGrid fields={buildFields(business, BUSINESS_KEYS, resolveDisplay)} />
                </SectionCard>

                <SectionCard
                  icon={<FaBoxOpen />}
                  tone="blue"
                  title="Products to Manufacture"
                  subtitle="Line items requested"
                >
                  {products.length ? (
                    products.map((row, idx) => (
                      <div key={`product-${idx}`} className="cm-rfv-subblock">
                        <h3 className="cm-rfv-subblock-title">Product {idx + 1}</h3>
                        <InfoGrid fields={buildFields(row, PRODUCT_KEYS, resolveDisplay)} />
                      </div>
                    ))
                  ) : (
                    <p className="cm-rfv-empty">No products listed.</p>
                  )}
                </SectionCard>

                <SectionCard
                  icon={<FaCalculator />}
                  tone="orange"
                  title="Base Price"
                  subtitle="Target pricing and tax"
                >
                  <InfoGrid fields={buildFields(basePrice, BASE_PRICE_KEYS, resolveDisplay)} />
                </SectionCard>

                <SectionCard
                  icon={<FaClipboardList />}
                  tone="blue"
                  title="Technical Requirements"
                  subtitle="Materials, process, and specifications"
                >
                  <InfoGrid fields={buildFields(technical, null, resolveDisplay)} />
                </SectionCard>

                {attachmentFields.length ? (
                  <SectionCard
                    icon={<FaPaperclip />}
                    tone="blue"
                    title="Attachments"
                    subtitle="Documents submitted with this RFQ"
                  >
                    <div className="cm-rfv-grid">
                      {attachmentFields.map(({ key, label, url }) => (
                        <div className="cm-rfv-field" key={key}>
                          <span className="cm-rfv-label">{label}</span>
                          <a className="cm-rfv-link" href={url} target="_blank" rel="noreferrer">
                            View file
                          </a>
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                ) : null}
              </div>

              <div className="cm-rfv-col">
                <SectionCard
                  icon={<FaIndustry />}
                  tone="blue"
                  title="Project Overview"
                  subtitle="Manufacturing scope and timeline"
                >
                  <InfoGrid fields={buildFields(project, PROJECT_KEYS, resolveDisplay)} />
                </SectionCard>

                <SectionCard
                  icon={<FaDollarSign />}
                  tone="blue"
                  title="Commercial Terms"
                  subtitle="Pricing model, payment, and delivery"
                >
                  <InfoGrid fields={buildFields(commercial, COMMERCIAL_KEYS, resolveDisplay)} />
                </SectionCard>

                {tierBreaks.length ? (
                  <SectionCard
                    icon={<FaLayerGroup />}
                    tone="orange"
                    title="Tier Breaks"
                    subtitle="Volume pricing tiers"
                  >
                    {tierBreaks.map((tier, idx) => (
                      <div key={`tier-${idx}`} className="cm-rfv-subblock">
                        <h3 className="cm-rfv-subblock-title">
                          {tier.tierLabel || `Tier ${idx + 1}`}
                        </h3>
                        <InfoGrid fields={buildFields(tier, TIER_KEYS, resolveDisplay)} />
                      </div>
                    ))}
                  </SectionCard>
                ) : null}

                <SectionCard
                  icon={<FaShieldAlt />}
                  tone="blue"
                  title="Quality & Compliance"
                  subtitle="Certifications, standards, and inspection"
                >
                  <InfoGrid fields={buildFields(quality, QUALITY_KEYS, resolveDisplay)} />
                </SectionCard>

                <SectionCard
                  icon={<FaBox />}
                  tone="blue"
                  title="Packaging & Shipping"
                  subtitle="Packaging and transport preferences"
                >
                  <InfoGrid fields={buildFields(packaging, PACKAGING_KEYS, resolveDisplay)} />
                </SectionCard>

                <SectionCard
                  icon={<FaSyncAlt />}
                  tone="blue"
                  title="Sample Requirements"
                  subtitle="Sample offer and return policy"
                >
                  <InfoGrid fields={buildFields(sample, SAMPLE_KEYS, resolveDisplay)} />
                </SectionCard>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
